import * as Service from '@/App/Service';
import ChalkySticks from '@chalkysticks/sdk';
import { ActionContext } from 'vuex';

/**
 * Actions for the Subscription store module.
 *
 * These actions interact with the SDK's Subscription utility directly
 * for most operations. The Service.Subscription is used only for
 * app-specific operations like user data sync.
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Store/Module/Subscription
 * @project ChalkySticks
 */
const actions = {
	/**
	 * Clear all subscription data
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @return Promise<void>
	 */
	async clearAll({ commit }: ActionContext<IRevenueCatState, any>): Promise<void> {
		commit('clearAll');
		ChalkySticks.Utility.Subscription.clearCache();
		ChalkySticks.Utility.Log.info('Subscription', 'Cleared all subscription data');
	},

	/**
	 * Fetch and update customer information
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @param boolean forceRefresh
	 * @return Promise<void>
	 */
	async fetchCustomerInfo(
		{ commit, state }: ActionContext<IRevenueCatState, any>,
		forceRefresh: boolean = false,
	): Promise<void> {
		commit('setError', null);

		try {
			const customerInfo = await ChalkySticks.Utility.Subscription.getCustomerInfo(forceRefresh);
			commit('setCustomerInfo', customerInfo);
			commit('setLastStatusCheck', Date.now());

			ChalkySticks.Utility.Log.info('Subscription', 'Customer info updated');
			ChalkySticks.Core.Event.Bus.dispatch('subscription:status.updated', customerInfo);
		}
		catch (error: any) {
			const errorMessage = error?.message || 'Failed to fetch customer info';
			commit('setError', errorMessage);

			ChalkySticks.Utility.Log.error('Subscription', 'Failed to fetch customer info:', error);
		}
	},

	/**
	 * Fetch available offerings from subscription provider
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @param boolean forceRefresh
	 * @return Promise<void>
	 */
	async fetchOfferings(
		{ commit, state }: ActionContext<IRevenueCatState, any>,
		forceRefresh: boolean = false,
	): Promise<void> {
		// Don't fetch if already loading
		if (state.isLoadingOfferings && !forceRefresh) {
			return;
		}

		commit('setIsLoadingOfferings', true);
		commit('setError', null);

		try {
			const offerings = await ChalkySticks.Utility.Subscription.getOfferings(forceRefresh);
			commit('setAvailableOfferings', offerings);

			ChalkySticks.Utility.Log.info('Subscription', 'Offerings fetched successfully');
			ChalkySticks.Core.Event.Bus.dispatch('subscription:offerings.fetched', offerings);
		}
		catch (error: any) {
			const errorMessage = error?.message || 'Failed to fetch offerings';
			commit('setError', errorMessage);

			ChalkySticks.Utility.Log.error('Subscription', 'Failed to fetch offerings:', error);
			ChalkySticks.Core.Event.Bus.dispatch('subscription:offerings.error', error);
		}
		finally {
			commit('setIsLoadingOfferings', false);
		}
	},

	/**
	 * Initialize subscription module
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @return Promise<void>
	 */
	async initialize({ dispatch }: ActionContext<IRevenueCatState, any>): Promise<void> {
		// Check if subscription system is available
		if (!ChalkySticks.Utility.Subscription.isAvailable()) {
			ChalkySticks.Utility.Log.info('Subscription', 'Subscription not available on this platform');
			return;
		}

		// Fetch initial data
		await Promise.all([dispatch('fetchCustomerInfo'), dispatch('fetchOfferings')]);
	},

	/**
	 * Login user with subscription provider
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @param string userId
	 * @return Promise<void>
	 */
	async login({ dispatch }: ActionContext<IRevenueCatState, any>, userId: string): Promise<void> {
		try {
			await ChalkySticks.Utility.Subscription.login(userId);
			await dispatch('fetchCustomerInfo', true);

			ChalkySticks.Utility.Log.info('Subscription', `User ${userId} logged in`);
		}
		catch (error) {
			ChalkySticks.Utility.Log.error('Subscription', 'Failed to login:', error);
		}
	},

	/**
	 * Logout user from subscription provider
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @return Promise<void>
	 */
	async logout({ dispatch, commit }: ActionContext<IRevenueCatState, any>): Promise<void> {
		try {
			await ChalkySticks.Utility.Subscription.logout();
			commit('clearAll');

			ChalkySticks.Utility.Log.info('Subscription', 'User logged out');
		}
		catch (error) {
			ChalkySticks.Utility.Log.error('Subscription', 'Failed to logout:', error);
		}
	},

	/**
	 * Purchase a subscription package
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @param ISubscriptionPackage purchasePackage
	 * @return Promise<boolean>
	 */
	async purchasePackage(
		{ commit, dispatch, state }: ActionContext<IRevenueCatState, any>,
		purchasePackage: ISubscriptionPackage,
	): Promise<boolean> {
		// Don't allow multiple simultaneous purchases
		if (state.isPurchasing) {
			ChalkySticks.Utility.Log.warn('Subscription', 'Purchase already in progress');
			return false;
		}

		commit('setIsPurchasing', true);
		commit('setError', null);
		commit('setSelectedPackage', purchasePackage);

		try {
			const result = await ChalkySticks.Utility.Subscription.purchasePackage(purchasePackage);

			if (result) {
				commit('setCustomerInfo', result.customerInfo);
				commit('setActivePackage', purchasePackage);

				ChalkySticks.Utility.Log.info('Subscription', 'Purchase successful');
				ChalkySticks.Core.Event.Bus.dispatch('subscription:purchase.success', result);

				// Update user data in authentication store
				await dispatch('authentication/updateUser', null, { root: true });

				return true;
			}

			return false;
		}
		catch (error: any) {
			const errorMessage = error?.message || 'Purchase failed';
			commit('setError', errorMessage);

			// Check if user cancelled
			if (error?.code === 'PURCHASE_CANCELLED_ERROR') {
				ChalkySticks.Utility.Log.info('Subscription', 'Purchase cancelled by user');
				ChalkySticks.Core.Event.Bus.dispatch('subscription:purchase.cancelled');
			}
			else {
				ChalkySticks.Utility.Log.error('Subscription', 'Purchase failed:', error);
				ChalkySticks.Core.Event.Bus.dispatch('subscription:purchase.error', error);
			}

			return false;
		}
		finally {
			commit('setIsPurchasing', false);
			commit('setSelectedPackage', null);
		}
	},

	/**
	 * Refresh subscription status
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @return Promise<void>
	 */
	async refreshStatus({ dispatch, state }: ActionContext<IRevenueCatState, any>): Promise<void> {
		// Only refresh if it's been more than 5 minutes
		const fiveMinutes = 5 * 60 * 1000;
		const shouldRefresh = !state.lastStatusCheck || Date.now() - state.lastStatusCheck > fiveMinutes;

		if (shouldRefresh) {
			await dispatch('fetchCustomerInfo', true);
		}
	},

	/**
	 * Restore previous purchases
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @return Promise<boolean>
	 */
	async restorePurchases({ commit, dispatch, state }: ActionContext<IRevenueCatState, any>): Promise<boolean> {
		// Don't allow multiple simultaneous restores
		if (state.isRestoring) {
			ChalkySticks.Utility.Log.warn('Subscription', 'Restore already in progress');
			return false;
		}

		commit('setIsRestoring', true);
		commit('setError', null);

		try {
			const customerInfo = await ChalkySticks.Utility.Subscription.restorePurchases();

			if (customerInfo) {
				commit('setCustomerInfo', customerInfo);

				const hasActiveSubscriptions = Object.keys(customerInfo.entitlements.active).length > 0;

				if (hasActiveSubscriptions) {
					ChalkySticks.Utility.Log.info('Subscription', 'Purchases restored successfully');
					ChalkySticks.Core.Event.Bus.dispatch('subscription:restore.success', customerInfo);

					// Update user data
					await dispatch('authentication/updateUser', null, { root: true });
				}
				else {
					ChalkySticks.Utility.Log.info('Subscription', 'No purchases to restore');
					ChalkySticks.Core.Event.Bus.dispatch('subscription:restore.empty');
				}

				return hasActiveSubscriptions;
			}

			return false;
		}
		catch (error: any) {
			const errorMessage = error?.message || 'Failed to restore purchases';
			commit('setError', errorMessage);

			ChalkySticks.Utility.Log.error('Subscription', 'Failed to restore purchases:', error);
			ChalkySticks.Core.Event.Bus.dispatch('subscription:restore.error', error);

			return false;
		}
		finally {
			commit('setIsRestoring', false);
		}
	},

	/**
	 * Sync user data with subscription provider
	 *
	 * @param ActionContext<IRevenueCatState, any> context
	 * @param ChalkySticks.Model.User user
	 * @return Promise<void>
	 */
	async syncUserData(
		{ dispatch }: ActionContext<IRevenueCatState, any>,
		user: ChalkySticks.Model.User,
	): Promise<void> {
		if (!user) {
			return;
		}

		try {
			await Service.Subscription.syncUserData(user);
			await dispatch('fetchCustomerInfo', true);

			ChalkySticks.Utility.Log.info('Subscription', 'User data synced');
		}
		catch (error) {
			ChalkySticks.Utility.Log.error('Subscription', 'Failed to sync user data:', error);
		}
	},
};

export default actions;
